import {
  BarChart3,
  Building2,
  CalendarRange,
  ClipboardCheck,
  ClipboardList,
  FileWarning,
  Gauge,
  Handshake,
  Layers,
  Presentation,
  LayoutDashboard,
  Scale,
  ScrollText,
  Star,
  Target,
  Users,
  UsersRound,
} from 'lucide-react';
import { canManageKraLibrary, type Role } from '@cfl/shared';

/**
 * Navigation model.
 *
 * Grouped by *whose work it is* rather than by entity type. The Kredily reference
 * stacks three levels of tabs (KRA > Add KRA / Create Group / Assign KRA) which
 * buries the actual task; here each destination is a thing a person came to do,
 * and sections a role has no business in are hidden rather than shown disabled.
 */

export interface NavVisibilityContext {
  role: Role;
  /** True when this person has at least one direct report. */
  hasReports: boolean;
}

export interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  /**
   * One line on what this destination is for, shown by the welcome tour. Required,
   * not optional: a new nav item without one would silently render as a bare title
   * in the tour, and a type error is a cheaper reminder than noticing that later.
   */
  description: string;
  /** Names an entry in the badges map; the pill hides when the count is 0. */
  badgeKey?: string;
  visible: (ctx: NavVisibilityContext) => boolean;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

const isHr = (role: Role) => role === 'HR' || role === 'SUPER_ADMIN';

export const NAV_SECTIONS: NavSection[] = [
  {
    label: 'My work',
    items: [
      {
        title: 'Dashboard',
        href: '/dashboard',
        icon: LayoutDashboard,
        description: 'Where you land: what is open now, what is waiting on you, and by when.',
        visible: () => true,
      },
      {
        title: 'My goals',
        href: '/goals',
        icon: Target,
        description:
          'The organization goals your manager sets for you — at least 3, weightage totalling 100 — and your own development goals, which carry 0 weightage and never affect the rating.',
        badgeKey: 'goalsPendingAcceptance',
        visible: () => true,
      },
      {
        title: 'My assessments',
        href: '/assessments',
        icon: ClipboardList,
        description:
          'Your quarterly self-assessment. Windows open and close at midnight Asia/Kolkata, so 23:30 IST on the last day still counts.',
        badgeKey: 'selfAssessmentOpen',
        visible: () => true,
      },
      {
        title: 'Recognition',
        href: '/recognition',
        icon: Star,
        description: 'Recognition you have given and received, outside the appraisal cycle.',
        visible: () => true,
      },
      {
        title: 'Dev Connect',
        href: '/dev-connect',
        icon: Presentation,
        description:
          'The Friday showcase sign-up sheet. Claim a slot to show what you shipped, and read the log of every session that has run. Everyone can see it; who may book is set per session.',
        visible: () => true,
      },
    ],
  },
  {
    label: 'My team',
    items: [
      // Keyed on hasReports, NOT role === 'MANAGER'. Manager-ness is a fact about
      // the reporting tree; someone can hold role EMPLOYEE and still have reports.
      {
        title: 'Team',
        href: '/team',
        icon: UsersRound,
        description: 'Your direct reports, their goals and where each one stands this quarter.',
        visible: (c) => c.hasReports,
      },
      {
        title: 'Reviews to do',
        href: '/team/reviews',
        icon: ClipboardCheck,
        description:
          'Stage 2 of the chain. The clock is 3 days from each employee’s own submission, so two reports who submit days apart have different deadlines.',
        badgeKey: 'managerReviewsPending',
        visible: (c) => c.hasReports,
      },
      // The same page HR gets under their own section. Two entries rather than one
      // shared item because a manager must not be shown a section headed "HR", and
      // `!isHr` is what keeps an HR user with reports from meeting it twice.
      //
      {
        title: 'Goal assignments',
        href: '/team/goals',
        icon: Target,
        description:
          'Your reports’ goals: who still has none, assigning a KRA group to several at once, and publishing the drafts so they go live.',
        visible: (c) => c.hasReports && !isHr(c.role),
      },
      {
        title: 'Manage KRA',
        href: '/team/kra',
        icon: Layers,
        description:
          'Write KRAs and weight them into a group totalling 100%. Assigning that group to people is one click away on Goal assignments.',
        visible: (c) => canManageKraLibrary(c) && !isHr(c.role),
      },
      {
        title: '1-on-1s',
        href: '/team/meetings',
        icon: Handshake,
        description: 'Schedule 1-on-1s with your reports and keep the agenda and notes with them.',
        visible: (c) => c.hasReports,
      },
    ],
  },
  {
    label: 'Department',
    items: [
      /**
       * Read-only oversight. Dept Head is not an approval gate in the review chain,
       * and deliberately gets no Calibration entry: that screen writes grades, and
       * they see its outcome in the distribution chart here.
       *
       * This used to say calibration was not a separate destination at all, on the
       * grounds that the 9-box and the distribution WERE the calibration view. They
       * are not - they report what the ratings came out as. Deciding who moves band,
       * against a target, with a reason recorded, is a different task, and it now has
       * its own screen under HR.
       */
      // DEPT_HEAD only. /department and /hr/analytics render the same AnalyticsPage, so
      // showing both to HR put one screen in their sidebar twice under two names. HR
      // keeps "Analytics"; a Department Head, who has no HR section, keeps this one.
      {
        title: 'Overview',
        href: '/department',
        icon: Gauge,
        description:
          'Read-only oversight of your department: 9-box, rating distribution and skill gaps. Department Head is not an approval gate — nothing here blocks or advances a review.',
        visible: (c) => c.role === 'DEPT_HEAD' && !isHr(c.role),
      },
    ],
  },
  {
    label: 'HR',
    items: [
      {
        title: 'Employees',
        href: '/hr/employees',
        icon: Users,
        description:
          'Onboard people and maintain department, designation, reporting line and role. Changing someone’s reporting manager is what moves their reviewer — reassignment is HR’s job, not an override.',
        visible: (c) => isHr(c.role),
      },
      // Not /hr/goals: the same screen serves managers, so it lives outside the HR
      // role gate. The href is shared with the "My team" entry above, which is why
      // that one is hidden from HR.
      //
      {
        title: 'Goal assignments',
        href: '/team/goals',
        icon: Target,
        description:
          'Goal coverage across the company: who still has none, assigning a KRA group to many people at once, and publishing the drafts.',
        visible: (c) => isHr(c.role),
      },
      /**
       * Kept in the sidebar, and the two screens link to each other.
       *
       * Taking it out was tried and reverted: writing a group and assigning it are one
       * job done in two places, so removing either entry just moves the hop somewhere
       * else. What was actually wrong was the old "Assign KRA" TAB inside this page - a
       * second list of the same employees with different scoping from Goal assignments,
       * which showed a manager all 71 people. That list is gone; the destination stays.
       */
      {
        title: 'Manage KRA',
        href: '/hr/kra-groups',
        icon: Layers,
        description:
          'The KRA library: write a KRA, weight KRAs into a group totalling 100%. Saving a group pinned to job titles assigns it straight away; Goal assignments is linked from here for anyone else.',
        visible: (c) => isHr(c.role),
      },
      {
        title: 'HR reviews',
        href: '/hr/reviews',
        icon: ClipboardCheck,
        description: 'Stage 3 of the chain: assessments the reporting manager has signed off that now need HR.',
        badgeKey: 'hrReviewsPending',
        visible: (c) => isHr(c.role),
      },
      {
        title: 'Calibration',
        href: '/hr/calibration',
        icon: Scale,
        description:
          'Rank everyone by their rating, compare the bands against the expected distribution, and move people between grades with a recorded reason. Nothing moves automatically — the curve is a guideline, not a rule.',
        visible: (c) => isHr(c.role),
      },
      {
        title: 'Analytics',
        href: '/hr/analytics',
        icon: BarChart3,
        description: '9-box, bell curve and skill gaps across every department.',
        visible: (c) => isHr(c.role),
      },
      {
        title: 'Improvement plans',
        href: '/hr/pip',
        icon: FileWarning,
        description: 'Performance improvement plans: who is on one, the checkpoints and the outcome.',
        visible: (c) => isHr(c.role),
      },
    ],
  },
  {
    label: 'Management',
    items: [
      {
        title: 'Final ratings',
        href: '/admin/final-ratings',
        icon: ClipboardCheck,
        description: 'Stage 4, the last gate: the management sign-off that fixes an employee’s final rating.',
        badgeKey: 'managementReviewsPending',
        visible: (c) => c.role === 'SUPER_ADMIN',
      },
    ],
  },
  /**
   * Setup, last and deliberately apart.
   *
   * Three screens that are configured and then left alone: the quarters are opened four
   * times a year, departments and job titles change when the org does, and the audit log
   * is read when something is disputed. Sitting them beside Reviews and Analytics made
   * an HR sidebar fifteen entries long, most of which nobody opens in a given month.
   *
   * Grouped rather than removed. Every one of them is the only place its job can be
   * done, so hiding them would cost real work; moving them out of the daily flow costs
   * nothing but one extra glance on the day you need them.
   */
  {
    label: 'Setup',
    items: [
      {
        title: 'Cycle & quarters',
        href: '/hr/cycles',
        icon: CalendarRange,
        description:
          'Open and close the quarters of the April–March financial year, and grant a per-employee window override when someone genuinely missed one.',
        visible: (c) => isHr(c.role),
      },
      {
        title: 'Organization',
        href: '/admin/organization',
        icon: Building2,
        description: 'Departments, designations and organization-wide settings.',
        visible: (c) => c.role === 'SUPER_ADMIN',
      },
      {
        title: 'Audit log',
        href: '/admin/audit',
        icon: ScrollText,
        description: 'Every change to cycles, goals and people — who made it, and when.',
        visible: (c) => c.role === 'SUPER_ADMIN',
      },
    ],
  },
];

/** Sections with their invisible items stripped, and empty sections dropped. */
export function visibleNavSections(ctx: NavVisibilityContext): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => item.visible(ctx)),
  })).filter((section) => section.items.length > 0);
}

/** A nav count pill: the number, and whether it is an urgent (red) queue. */
export interface NavBadge {
  count: number;
  urgent?: boolean;
}

export type NavBadges = Partial<Record<string, NavBadge>>;

/** Longest matching href wins, so /team/reviews doesn't also light up /team. */
export function isActivePath(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}
